import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, BarChart3, Rss, Trophy, User, Headphones, Swords,
  Settings, LogOut, Shield, HelpCircle, Menu, X, Share2, MessageCircle, Brain, Globe
} from 'lucide-react';
import { useLang } from '@/utils/i18n';
import { clearAuth } from '@/utils/auth';

const NAV_ITEMS = [
  { path: '/dashboard', icon: Home, labelDe: 'Home', labelEn: 'Home' },
  { path: '/feed', icon: Rss, labelDe: 'Feed', labelEn: 'Feed' },
  { path: '/analytics', icon: BarChart3, labelDe: 'Odds', labelEn: 'Odds' },
  { path: '/leaderboard', icon: Trophy, labelDe: 'Ranking', labelEn: 'Ranking' },
  { path: '/profile', icon: User, labelDe: 'Profil', labelEn: 'Profile' },
];

const MENU_ITEMS = [
  { path: '/chat', icon: MessageCircle, labelDe: 'Live Chat', labelEn: 'Live Chat' },
  { path: '/social', icon: Trophy, labelDe: 'Community', labelEn: 'Community' },
  { path: '/tip-sharing', icon: Share2, labelDe: 'Tip-Sharing', labelEn: 'Tip Sharing' },
  { path: '/messages', icon: MessageCircle, labelDe: 'Nachrichten', labelEn: 'Messages' },
  { path: '/ai-challenges', icon: Swords, labelDe: 'User vs KI', labelEn: 'User vs AI' },
  { path: '/multi-sport', icon: Globe, labelDe: 'Multi-Sport', labelEn: 'Multi-Sport' },
  { path: '/challenges', icon: Swords, labelDe: 'Challenges', labelEn: 'Challenges' },
  { path: '/support', icon: Headphones, labelDe: 'Support', labelEn: 'Support' },
  { path: '/faq', icon: HelpCircle, labelDe: 'FAQ', labelEn: 'FAQ' },
  { path: '/admin', icon: Shield, labelDe: 'Admin', labelEn: 'Admin' },
];

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 bg-[#1E1E1E] border-t border-white/10 z-50 safe-area-bottom"
    >
      <div className="flex items-center justify-around h-14">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center w-full h-full transition-colors ${
                isActive ? 'text-[#00BFA6]' : 'text-gray-500'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 font-medium">
                {lang === 'de' ? item.labelDe : item.labelEn}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export const MobileMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { lang } = useLang();

  const handleNav = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[60] md:hidden"
        onClick={onClose}
        data-testid="mobile-menu-backdrop"
      />
      {/* Slide-out panel */}
      <div
        data-testid="mobile-menu-panel"
        className="fixed top-0 right-0 bottom-0 w-64 bg-[#1E1E1E] border-l border-white/10 z-[61] md:hidden overflow-y-auto"
        style={{ animation: 'slideInRight 0.2s ease-out' }}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <span className="text-sm font-bold text-white">{lang === 'de' ? 'Menü' : 'Menu'}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="py-2">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                data-testid={`menu-${item.path.slice(1)}`}
                onClick={() => handleNav(item.path)}
                className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 text-[#00BFA6]" />
                <span className="text-sm">{lang === 'de' ? item.labelDe : item.labelEn}</span>
              </button>
            );
          })}
          <div className="border-t border-white/10 mt-2 pt-2">
            <button
              data-testid="menu-settings"
              onClick={() => handleNav('/dashboard?settings=1')}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
            >
              <Settings className="w-5 h-5 text-gray-500" />
              <span className="text-sm">{lang === 'de' ? 'Einstellungen' : 'Settings'}</span>
            </button>
            <button
              data-testid="menu-logout"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">{lang === 'de' ? 'Abmelden' : 'Logout'}</span>
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export const MobileMenuButton = ({ onClick }) => (
  <button
    data-testid="mobile-menu-btn"
    onClick={onClick}
    className="md:hidden text-gray-400 hover:text-white p-2"
  >
    <Menu className="w-5 h-5" />
  </button>
);
